import Actions from 'actions'
import { PatientSidebarItemKeys } from 'components/Patients/PatientSwitchableView/PatientSidebar/patientSidebarConsts'
import { setActiveItem } from 'components/Patients/PatientSwitchableView/PatientSidebar/patientSidebarSlice'
import { PATIENT_CARD_TABS } from 'consts/patientCard'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { logInfo } from 'utils/logUtils'
import useFeatureFlags from './useFeatureFlags'
import useQueryParams from './useQueryParams'

// Track render frequency across all instances
const renderStats = { count: 0, lastLogTime: Date.now() }
const LOG_INTERVAL_MS = 30000

const usePatientTags = ({
  patient,
  tagsTypes = ['program', 'status', 'authStatus', 'treatment', 'scan', 'custom', 'overdue']
}) => {
  const dispatch = useDispatch()
  const { grinDesignRefresh: grinDesignRefreshFF } = useFeatureFlags()
  const { setQueryParams, queryParams } = useQueryParams()
  const doctor = useSelector(state => state.profileReducer.doctor)
  const userId = doctor?.user?.id
  const isFirstRender = useRef(true)

  // Track render frequency and log periodically
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    renderStats.count += 1
    const now = Date.now()

    if (now - renderStats.lastLogTime >= LOG_INTERVAL_MS && renderStats.count > 0) {
      logInfo('[usePatientTags] Render frequency report', {
        component: 'usePatientTags',
        event: 'render_frequency',
        renderCount: renderStats.count,
        intervalMs: LOG_INTERVAL_MS,
        patientId: patient?.id,
        userId
      })
      renderStats.count = 0
      renderStats.lastLogTime = now
    }
  })

  const handleAsigneeTagClicked = useCallback(
    assigneeTag => {
      if (grinDesignRefreshFF) {
        setQueryParams({ ...queryParams, patientView: PatientSidebarItemKeys.Notes })
        dispatch(setActiveItem(PatientSidebarItemKeys.Notes))
      } else {
        dispatch(
          Actions.togglePatientCard({
            isModalOpen: true,
            patientId: patient.id,
            selectedTab: PATIENT_CARD_TABS.notes.index
          })
        )
      }

      const assigneeTagMetadata = JSON.parse(assigneeTag?.metadata || '{}')

      if (doctor.id === assigneeTagMetadata.assigneeId) {
        dispatch(Actions.setAssigneeSeenTag({ patientTag: assigneeTag }))
      }
    },
    [dispatch, doctor, grinDesignRefreshFF, patient, queryParams, setQueryParams]
  )

  const filteredPatientTags = useMemo(() => {
    const startTime = performance.now()
    const result =
      patient?.patientTags?.items.filter(patientTag => {
        if (patientTag._deleted) return false
        if (!patientTag.tag) return false
        if (!tagsTypes.find(tagType => tagType === patientTag.tag.type)) return false
        return true
      }) ?? []

    const duration = performance.now() - startTime
    if (duration > 5) {
      logInfo('[usePatientTags] PERF: Slow filteredPatientTags calculation', {
        component: 'usePatientTags',
        event: 'filter_tags_slow',
        durationMs: duration,
        inputCount: patient?.patientTags?.items?.length,
        outputCount: result?.length,
        patientId: patient?.id,
        userId
      })
    }

    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient, tagsTypes]) // userId intentionally omitted: only used inside logInfo, not in computation

  const availableTags = useMemo(() => filteredPatientTags.map(patientTag => patientTag.tag), [filteredPatientTags])

  const needsAttentionTag = useMemo(
    () => filteredPatientTags?.find(pt => pt.tag.value === 'needsAttention'),
    [filteredPatientTags]
  )

  const needsAttentionTagDetails = useMemo(
    () => (needsAttentionTag?.metadata ? JSON.parse(needsAttentionTag.metadata) : null),
    [needsAttentionTag]
  )

  const assigneeTag = useMemo(() => {
    const startTime = performance.now()
    const foundAssigneeTag = patient?.patientTags?.items.find(
      patientTag => patientTag?.tag?.type === 'assignee' && !patientTag._deleted
    )

    const assigneeTagMetadata = JSON.parse(foundAssigneeTag?.metadata || '{}')
    const result = foundAssigneeTag
      ? {
          ...foundAssigneeTag.tag,
          label: foundAssigneeTag.tag.value,
          onClick: () => handleAsigneeTagClicked(foundAssigneeTag),
          setUnreadIndicator: () => dispatch(Actions.setAssigneeSeenTag({ patientTag: foundAssigneeTag })),
          showUnseenIndicator: assigneeTagMetadata.unreadIndication === true,
          assigneeId: assigneeTagMetadata.assigneeId,
          noteId: assigneeTagMetadata.noteId
        }
      : null

    const duration = performance.now() - startTime
    if (duration > 5) {
      logInfo('[usePatientTags] PERF: Slow assigneeTag calculation', {
        component: 'usePatientTags',
        event: 'assignee_tag_slow',
        durationMs: duration,
        patientTagsCount: patient?.patientTags?.items?.length,
        patientId: patient?.id,
        userId
      })
    }

    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, handleAsigneeTagClicked, patient]) // userId intentionally omitted: only used inside logInfo

  return { tags: availableTags, assigneeTag, needsAttentionTag, needsAttentionTagDetails }
}

export default usePatientTags
